import { Button, Card, Divider, Flex, Input, Modal, Select, Table, TableProps } from 'antd';
import dayjs from 'dayjs';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import StatusComponent from 'components/status/status';
import NoData from 'components/noData/noData';
import ListButton from 'components/listButton/listButton';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import PageTitle from 'components/title/title';
import NumberElement from 'components/number/number';
import TableTotal from 'components/tableTotal/tableTotal';

// models
import { TransferApproval } from '../models/transaction';
import NotificationService from 'core/services/notification';
import { onGetTransferApprovals, onApproveTransfer, onRejectTransfer } from '../services/transactionsService';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import PriceElement from 'components/price/price';

interface ApprovalsComponentProps {}

const ApprovalsComponent: FunctionComponent<ApprovalsComponentProps> = () => {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isActioning, setIsActioning] = useState<boolean>(false);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const [approvals, setApprovals] = useState<TransferApproval[]>([]);

	const [status, setStatus] = useState<string>('');

	const [rejectModalOpen, setRejectModalOpen] = useState<boolean>(false);
	const [approveModalOpen, setApproveModalOpen] = useState<boolean>(false);
	const [viewNoteModalOpen, setViewNoteModalOpen] = useState<boolean>(false);
	const [selectedApproval, setSelectedApproval] = useState<TransferApproval | null>(null);
	const [rejectNote, setRejectNote] = useState<string>('');

	const columns: TableProps<TransferApproval>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'created_at',
			render: (date) => dayjs(date).format('DD/MM/YYYY, hh:mm A'),
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status, row) => (
				<StatusComponent
					text={isRTL() ? row.translated_status?.ar : row.translated_status?.en}
					color={StatusColors[status]}
				/>
			),
		},
		{
			title: t('ACTIONS.SELLER_NATIONAL_ID'),
			dataIndex: 'seller',
			key: 'seller_id',
			render: (seller) => seller?.national_id,
		},
		{
			title: t('ACTIONS.SELLER_NAME'),
			dataIndex: 'seller',
			key: 'seller_name',
			render: (seller) => isRTL() ? seller?.translated_name?.ar : seller?.translated_name?.en,
		},
		{
			title: t('ACTIONS.BUYER_NATIONAL_ID'),
			dataIndex: 'buyer',
			key: 'buyer_id',
			render: (buyer) => buyer?.national_id,
		},
		{
			title: t('ACTIONS.BUYER_NAME'),
			dataIndex: 'buyer',
			key: 'buyer_name',
			render: (buyer) => isRTL() ? buyer?.translated_name?.ar : buyer?.translated_name?.en,
		},
		{
			title: t('COMMON.NUM_OF_SHARES'),
			dataIndex: 'number_of_shares',
			key: 'number_of_shares',
		},
		{
			title: t('COMMON.SHARE_PRICE'),
			dataIndex: 'share_price',
			key: 'share_price',
			render: (price) => <PriceElement value={price} decimalScale={2} />,
		},
		// {
		// 	title: t('COMPANY_INFORMATION.TOTAL_AMOUNT'),
		// 	dataIndex: 'total_amount',
		// 	key: 'total_amount',
		// 	render: (amount) => <PriceElement value={amount} decimalScale={2} />,
		// },
		{
			title: t('COMPANY_INFORMATION.COMPANY_APPROVAL_STATUS'),
			dataIndex: 'company_approval_status',
			key: 'company_approval_status',
			render: (status, row) => (
				<StatusComponent
					text={isRTL() ? row.translated_company_approval_status?.ar : row.translated_company_approval_status?.en}
					color={StatusColors[status]}
				/>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			key: 'actions',
			render: (_, row) =>
				row.is_actionable ? (
					<Flex gap={8}>
						<ListButton
							type="accept"
							text={t('COMPANY_INFORMATION.APPROVE')}
							onClick={() => {
								setSelectedApproval(row);
								setApproveModalOpen(true);
							}}
						/>
						<ListButton
							type="reject"
							text={t('COMPANY_INFORMATION.REJECT')}
							onClick={() => {
								setSelectedApproval(row);
								setRejectNote('');
								setRejectModalOpen(true);
							}}
						/>
					</Flex>
				) : row.company_approval_status === 'Rejected' ? (
					<ListButton
						type="view"
						text={t('COMPANY_INFORMATION.REJECTION_NOTE')}
						onClick={() => {
							setSelectedApproval(row);
							setViewNoteModalOpen(true);
						}}
					/>
				) : null,
		},
	];

	useEffect(() => {
		fetchApprovals(1);
	}, [status]);

	const fetchApprovals = async (page: number) => {
		try {
			setIsLoading(true);
			const response = await onGetTransferApprovals(page, status);
			setApprovals(response.share_transactions);
			setTotalRecords(response.pagination.total);
			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const handleApprove = async () => {
		if (!selectedApproval) return;
		try {
			setIsActioning(true);
			await onApproveTransfer(selectedApproval.id);
			NotificationService.success(t('COMMON.SUCCESS'), t('COMPANY_INFORMATION.TRANSFER_APPROVED_SUCCESSFULLY'));
			setApproveModalOpen(false);
			setSelectedApproval(null);
			await fetchApprovals(currentPage);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ERROR'));
		} finally {
			setIsActioning(false);
		}
	};

	const handleReject = async () => {
		if (!selectedApproval) return;
		try {
			setIsActioning(true);
			await onRejectTransfer(selectedApproval.id, { note: rejectNote });
			NotificationService.success(t('COMMON.SUCCESS'), t('COMPANY_INFORMATION.TRANSFER_REJECTED_SUCCESSFULLY'));
			setRejectModalOpen(false);
			setSelectedApproval(null);
			setRejectNote('');
			await fetchApprovals(currentPage);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ERROR'));
		} finally {
			setIsActioning(false);
		}
	};

	return (
		<>
			<PageTitle name={t('COMPANY_INFORMATION.TRANSFER_APPROVALS')} />

			<Card className="mt-4">
				<div>
					<p className="text-light fw-medium mb-1">{t('COMMON.STATUS')}</p>
					<Select
						size="large"
						onChange={(val) => setStatus(val)}
						defaultValue={''}
						className="filled"
						options={[
							{ value: '', label: t('COMMON.ALL') },
							{ value: 'Pending', label: t('COMMON.PENDING') },
							{ value: 'Approved', label: t('COMMON.APPROVED') },
							{ value: 'Rejected', label: t('COMMON.REJECTED') },
						]}
					/>
				</div>

				<Divider />

				<Table
					columns={columns}
					dataSource={approvals}
					scroll={{ x: 600 }}
					rowKey="id"
					loading={isLoading}
					pagination={{
						pageSize: 15,
						total: totalRecords,
						showSizeChanger: false,
						current: currentPage,
						onChange: fetchApprovals,
						showTotal: (total) => <TableTotal total={total} />,
					}}
					locale={{ emptyText: <NoData /> }}
				/>
			</Card>

			<ConfirmModalComponent
				open={approveModalOpen}
				title={t('COMPANY_INFORMATION.APPROVE')}
				description={t('COMPANY_INFORMATION.APPROVE_CONFIRMATION')}
				confirmText={t('COMPANY_INFORMATION.APPROVE')}
				confirmColor="green"
				onDiscard={() => {
					setApproveModalOpen(false);
					setSelectedApproval(null);
				}}
				onConfirm={handleApprove}
				isLoading={isActioning}
			/>

			<ConfirmModalComponent
				open={rejectModalOpen}
				title={t('COMPANY_INFORMATION.REJECT')}
				description={t('COMPANY_INFORMATION.REJECT_CONFIRMATION')}
				confirmText={t('COMPANY_INFORMATION.REJECT')}
				confirmColor="danger"
				onDiscard={() => {
					setRejectModalOpen(false);
					setSelectedApproval(null);
					setRejectNote('');
				}}
				onConfirm={handleReject}
				isLoading={isActioning}>
				<div className="mt-3">
					<p className="text-light fw-medium mb-1">{t('COMMON.NOTE')}</p>
					<Input.TextArea rows={4} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder={t('COMMON.NOTE')} />
				</div>
			</ConfirmModalComponent>

			<Modal
				open={viewNoteModalOpen}
				title={t('COMPANY_INFORMATION.REJECTION_NOTE')}
				onCancel={() => {
					setViewNoteModalOpen(false);
					setSelectedApproval(null);
				}}
				footer={
					<Button
						variant="link"
						color="red"
						onClick={() => {
							setViewNoteModalOpen(false);
							setSelectedApproval(null);
						}}>
						{t('COMMON.CLOSE')}
					</Button>
				}>
				<p className="mt-3">{selectedApproval?.company_approval_note ?? t('COMMON.N_A')}</p>
			</Modal>
		</>
	);
};

export default ApprovalsComponent;
